import { useState } from 'react';
import type { StepProps } from '../../OBWWizard';

// Phase 5c.5 (ADR-030 D3) — jurisdiction-aware business detail capture.
// Registration number label/placeholder changes per jurisdiction.

type Jurisdiction = 'AU' | 'NZ' | 'US';

const REG_LABEL: Record<Jurisdiction, string> = {
  AU: 'ABN — Australian Business Number',
  NZ: 'NZBN — New Zealand Business Number',
  US: 'EIN — Employer Identification Number',
};

const REG_PLACEHOLDER: Record<Jurisdiction, string> = {
  AU: '11 222 333 444',
  NZ: '9429 0000 0000 0',
  US: 'XX-XXXXXXX',
};

// ⚠ These hints must NOT claim the number is verified against a registry, and must not
// name one. ⭐ ONE OF THE TWO REASONS WENT AWAY ON 17 SEPTEMBER 2026, AND THE OTHER DID
// NOT — which is exactly why the copy is unchanged. `verify_business_registration`
// (lambdas/pow-agent/src/tools.ts) is no longer a placeholder: it now calls the shared
// register client (ABR / ACNC / NZBN / OpenSOSData). But `nexa/abr-api-key`,
// `nexa/nzbn-api-key` and `nexa/opensosdata-api-key` STILL do not exist in Secrets
// Manager, so every lookup returns `outcome: 'unavailable'` and nothing is checked for
// any applicant in any jurisdiction. A working integration with no key is still a
// promise we cannot keep.
//
// ⛔ AND EVEN ONCE A KEY EXISTS, THE NUMBER ITSELF IS NOT WHAT GETS CHECKED. The lookup
// is BY NAME; the number is compared against whatever the register discloses, which for
// US is `not_comparable` by construction (this form collects a federal EIN, the register
// returns a state filing id). So "we verify your <number>" would be wrong for US even
// fully provisioned. Re-add a claim per jurisdiction only when that jurisdiction's key
// is live AND the claim matches what is actually compared.
const REG_HINT: Record<Jurisdiction, string> = {
  AU: 'Required for AU partners. Enter your ABN exactly as registered.',
  NZ: 'Required for NZ partners. Enter your NZBN exactly as registered.',
  US: 'Required for US partners. Enter your EIN exactly as issued.',
};

const DESC_MIN = 100;
const DESC_MAX = 500;

export function PartnerBusinessDetailStep({ data, onNext }: StepProps) {
  const [jurisdiction, setJurisdiction] = useState<Jurisdiction>(
    (data?.jurisdiction as Jurisdiction) ?? 'AU',
  );
  const [registrationNumber, setRegistrationNumber] = useState<string>(
    (data?.registrationNumber as string) ?? '',
  );
  const [description, setDescription] = useState<string>(
    (data?.descriptionOfPractice as string) ?? '',
  );
  const [error, setError] = useState<string | null>(null);

  const descLen = description.trim().length;
  const descValid = descLen >= DESC_MIN && descLen <= DESC_MAX;
  const regValid = registrationNumber.trim().length > 0;

  function commit() {
    if (!regValid) { setError(`Please enter your ${REG_LABEL[jurisdiction].split(' — ')[0]}.`); return; }
    if (!descValid) {
      setError(`Description must be between ${DESC_MIN} and ${DESC_MAX} characters (currently ${descLen}).`);
      return;
    }
    setError(null);
    onNext({
      jurisdiction,
      registrationNumber: registrationNumber.trim(),
      descriptionOfPractice: description.trim(),
    });
  }

  return (
    <div className="obw-step-content">
      <h2 className="obw-title">Tell us about your practice</h2>
      <p className="obw-subtitle">
        These details feed the suitability assessment. Your registration number is recorded
        with your application and forms part of that assessment; the description helps us
        understand who you work with and what services you offer.
      </p>

      <p className="obw-form-section">Jurisdiction</p>
      <div className="obw-form-group">
        <label className="obw-form-label" htmlFor="pa-jurisdiction">Country</label>
        <select
          id="pa-jurisdiction"
          className="obw-form-input"
          value={jurisdiction}
          onChange={(e) => setJurisdiction(e.target.value as Jurisdiction)}
        >
          <option value="AU">Australia</option>
          <option value="NZ">New Zealand</option>
          <option value="US">United States</option>
        </select>
      </div>

      <div className="obw-form-group">
        <label className="obw-form-label" htmlFor="pa-regNumber">
          {REG_LABEL[jurisdiction]} *
        </label>
        <input
          id="pa-regNumber"
          className="obw-form-input"
          type="text"
          value={registrationNumber}
          onChange={(e) => setRegistrationNumber(e.target.value)}
          placeholder={REG_PLACEHOLDER[jurisdiction]}
          required
          maxLength={64}
        />
        <p className="obw-form-hint">{REG_HINT[jurisdiction]}</p>
      </div>

      <p className="obw-form-section">Practice description</p>
      <div className="obw-form-group">
        <label className="obw-form-label" htmlFor="pa-description">
          Describe your practice ({DESC_MIN}–{DESC_MAX} characters) *
        </label>
        <textarea
          id="pa-description"
          className="obw-form-input"
          rows={6}
          minLength={DESC_MIN}
          maxLength={DESC_MAX}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Briefly describe your business, the kinds of clients you serve, and why you'd like to be a Nexanomics partner."
          style={{ resize: 'vertical', minHeight: '140px' }}
        />
        <p
          className="obw-form-hint"
          style={{
            color: descValid
              ? 'var(--success)'
              : descLen > DESC_MAX
                ? 'var(--danger)'
                : 'var(--text-dim)',
          }}
        >
          {descLen} / {DESC_MAX} characters
          {descLen < DESC_MIN && ` — at least ${DESC_MIN - descLen} more`}
        </p>
      </div>

      {error && <div className="portal-wizard-error" role="alert">{error}</div>}

      <div className="portal-wizard-nav">
        <button
          type="button"
          className="obw-btn-primary"
          onClick={commit}
          disabled={!regValid || !descValid}
        >
          Continue →
        </button>
      </div>
    </div>
  );
}
